import React, { useState, useMemo } from "react";
import { ChevronDown, Check, X, ArrowRight, Download } from "lucide-react";
import { cn } from "lib/utils";
import Graph from "../graph";
import DetailsTable from "./DetailsTable.js";
import GetQueryCsvApi from "../../api/getQueryCsvApi";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";

// Big graph fills at least 360px, growing to 55% of the viewport in the popup.
const BIG_GRAPH_HEIGHT = "max(360px, 55vh)";

// Row highlight color for the per-row severity the backend assigns from each
// query's instructions. Mirrors statusRowClass in requestUrls/RequestUrls.js.
export function severityRowClass(severity) {
    if (severity === "error") return "bg-destructive/10 text-destructive";
    if (severity === "warning") return "bg-warning/10 text-warning";
    return "";
}

function DetailsView({ open, onOpenChange, details, startTime, endTime, theme }) {
    const [showInstructions, setShowInstructions] = useState(false);

    const kind = details?.kind;
    const data = details?.data;
    const title = details?.title || "Details";
    const csv = details?.csv;
    const isMetric = kind === "metric";

    const downloadCsvFile = () => {
        GetQueryCsvApi(csv.queryId, csv.queryName, csv.eventId, csv.environment, startTime, endTime);
    };

    const renderInstructions = (items) =>
        items.map((item) => (
            <React.Fragment key={item.title}>
                <p className="font-medium">{item.title}</p>
                <ul className="ml-4 list-disc text-sm text-muted-foreground">
                    {item.details && item.details.map((detail) => <li key={detail}>{detail}</li>)}
                </ul>
            </React.Fragment>
        ));

    // row_severity is index-aligned with log_data; key by row object so the
    // lookup survives DetailsTable's pagination slicing.
    const severityByRow = useMemo(() => {
        const map = new Map();
        if (Array.isArray(data?.log_data) && Array.isArray(data?.row_severity)) {
            data.log_data.forEach((row, index) => map.set(row, data.row_severity[index]));
        }
        return map;
    }, [data]);

    const hasData = data && Object.keys(data).length > 0;
    const hasGraph = isMetric
        ? Array.isArray(data?.log_data) && data.log_data.length > 0
        : hasData && data.health_card?.includes("graph");
    const hasTable = !isMetric && Array.isArray(data?.log_data);
    const hasAlerts = !isMetric && data?.alert_list?.length > 0;
    const hasInstructions = !isMetric && !!data?.instructions;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[92vh] w-[97vw] max-w-[97vw] flex-col gap-0 p-0">
                <DialogHeader className="shrink-0 space-y-0 border-b border-border px-6 py-4">
                    <div className="flex items-start justify-between gap-4 pr-10">
                        <div className="space-y-1">
                            <DialogTitle>{title}</DialogTitle>
                            <p className="text-sm text-muted-foreground">
                                {startTime} → {endTime}
                            </p>
                        </div>
                        {csv && (
                            <Button
                                size="icon-sm"
                                onClick={downloadCsvFile}
                                title="Download CSV"
                            >
                                <Download />
                            </Button>
                        )}
                    </div>
                </DialogHeader>

                <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
                    {!hasData ? (
                        <p className="text-sm text-muted-foreground">No data to display.</p>
                    ) : isMetric ? (
                        <Graph
                            log_data={data.log_data}
                            key_list={data.key_list}
                            legend_enabled={true}
                            height={BIG_GRAPH_HEIGHT}
                        />
                    ) : (
                        <>
                            {hasInstructions && (
                                <div className="rounded-md border border-border">
                                    <button
                                        type="button"
                                        onClick={() => setShowInstructions((v) => !v)}
                                        className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-medium"
                                    >
                                        <span>How to read this</span>
                                        <ChevronDown
                                            className={cn(
                                                "h-4 w-4 transition-transform",
                                                showInstructions && "rotate-180"
                                            )}
                                        />
                                    </button>
                                    {showInstructions && (
                                        <div className="grid grid-cols-1 gap-4 border-t border-border p-4 sm:grid-cols-3">
                                            <div className="space-y-1">
                                                <h5 className="flex items-center gap-1.5 font-semibold text-success">
                                                    <Check className="h-4 w-4" /> What looks good
                                                </h5>
                                                {renderInstructions(data.instructions.good)}
                                            </div>
                                            <div className="space-y-1">
                                                <h5 className="flex items-center gap-1.5 font-semibold text-destructive">
                                                    <X className="h-4 w-4" /> What looks bad
                                                </h5>
                                                {renderInstructions(data.instructions.bad)}
                                            </div>
                                            <div className="space-y-1">
                                                <h5 className="flex items-center gap-1.5 font-semibold">
                                                    <ArrowRight className="h-4 w-4" /> What to do
                                                </h5>
                                                {renderInstructions(data.instructions.action)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {hasGraph && (
                                <Graph
                                    log_data={data.log_data}
                                    key_list={data.key_list}
                                    legend_enabled={true}
                                    height={BIG_GRAPH_HEIGHT}
                                />
                            )}

                            {hasTable && (
                                <DetailsTable
                                    logData={data.log_data}
                                    keyList={data.key_list}
                                    theme={theme}
                                    rowHighlightClass={(row) =>
                                        severityRowClass(severityByRow.get(row))
                                    }
                                />
                            )}

                            {hasAlerts && (
                                <Card>
                                    <CardContent className="overflow-x-auto p-4">
                                        <table className="w-full text-center text-sm">
                                            <thead>
                                                <tr className="border-b border-border text-muted-foreground">
                                                    {Object.keys(data.alert_list[0]).map((key) => (
                                                        <th scope="col" key={key} className="px-2 py-1.5 font-medium">
                                                            {key}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="text-xs">
                                                {data.alert_list.map((row, index) => (
                                                    <tr key={index} className="border-b border-border/60">
                                                        {Object.values(row).map((col, colIndex) => (
                                                            <td key={colIndex} className="px-2 py-1.5">{col}</td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </CardContent>
                                </Card>
                            )}
                        </>
                    )}
                </div>

                <DialogFooter className="shrink-0 border-t border-border px-6 py-4">
                    <DialogClose asChild>
                        <Button variant="secondary">Close</Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DetailsView;
